/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * usePageBuilder — useReducer-based state for the Page Builder editor.
 * Supports undo/redo, section CRUD, selection, and dirty-state tracking.
 */

import { useReducer, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

// ─── Initial section defaults by type ────────────────────────────────────────

export const SECTION_DEFAULTS = {
  // Holds a reference and nothing else. The server swaps it for the component's
  // own sections when the page is served, so an edit to the component reaches
  // every page including it — which a copied config could never do.
  component: {
    section_config: { component_ref: '' },
    layout_config: {},
  },
  hero: {
    section_config: {
      layoutType: 'centered',
      sectionHeight: 'auto',
      sectionHeightPx: 600,
      contentMaxWidth: 'medium',
      paddingTop: 80,
      paddingBottom: 80,
      textAlignment: 'center',
      bgType: 'gradient',
      bgColor: '#1e293b',
      bgImageUrl: '',
      bgImagePosition: 'center',
      bgImageSize: 'cover',
      bgVideoUrl: '',
      bgOverlayColor: '#000000',
      bgOverlayOpacity: 40,
      bgParallax: false,
      badgeVisible: false,
      badgeText: 'New Feature',
      badgeColor: '#7c3aed',
      badgeStyle: 'pill',
      badgeLink: '',
      headlineText: 'Build something great',
      headlineFontSize: 'xl',
      headlineColor: '#ffffff',
      headlineHighlightWord: '',
      headlineHighlightStyle: 'color',
      headlineHighlightColor: '#f59e0b',
      headlineTypewriter: false,
      headlineTypewriterWords: [],
      subheadlineVisible: true,
      subheadlineText: 'Everything you need to launch faster.',
      subheadlineFontSize: 'md',
      subheadlineColor: '#cbd5e1',
      primaryCTA: { label: 'Get Started', url: '/sign-up', icon: '', iconPosition: 'left', openNewTab: false, style: 'filled' },
      secondaryCTA: { visible: false, label: 'Learn More', url: '#', icon: '', style: 'outlined', openNewTab: false, isVideoTrigger: false, videoUrl: '' },
      buttonLayout: 'side-by-side',
      socialProofVisible: false,
      ratingVisible: false,
      ratingValue: 5,
      ratingCount: '2,400 reviews',
      userCountText: '50,000+ teams',
      avatarsVisible: false,
      avatarImages: [],
      logoStripVisible: false,
      logoStripImages: [],
      visualType: 'none',
      visualUrl: '',
      visualDeviceFrame: 'none',
      visualPosition: 'right',
      visualWidthPercent: 50,
      headlineFontFamily: '',
      bodyFontFamily: '',
      entranceAnimation: 'none',
      scrollIndicatorVisible: false,
      shapeDivider: 'none',
      shapeDividerColor: '#ffffff',
    },
    layout_config: { padding: 'xl', background: 'gradient', max_width: 'full' },
  },
  text_block: {
    section_config: {
      blockType: 'paragraph',
      text: '<p>Your content goes here.</p>',
      htmlTag: 'p',
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: 16,
      responsiveFontSize: { mobile: 14, tablet: 15 },
      lineHeight: 1.7,
      letterSpacing: 0,
      textTransform: 'none',
      textDecoration: 'none',
      textColor: '#374151',
      gradientText: false,
      gradientFrom: '#7c3aed',
      gradientTo: '#2563eb',
      gradientAngle: 135,
      highlightVisible: false,
      highlightColor: '#fde68a',
      textAlign: 'left',
      textAlignMobile: 'left',
      direction: 'ltr',
      horizontalAlign: 'left',
      marginTop: 0,
      marginBottom: 0,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      maxWidth: 'full',
      bgVisible: false,
      bgColor: '#f8fafc',
      bgOpacity: 100,
      borderVisible: false,
      borderColor: '#e2e8f0',
      borderWidth: 1,
      borderStyle: 'solid',
      borderRadius: 0,
      borderSide: 'all',
      listStyle: 'bullet',
      listItems: ['First item', 'Second item', 'Third item'],
      listSpacing: 'normal',
      listIconColor: '#7c3aed',
      quoteStyle: 'left-border',
      quoteAccentColor: '#7c3aed',
      quoteAuthor: '',
      quoteAuthorVisible: false,
      quoteAuthorRole: '',
      linkColor: '#2563eb',
      linkHoverColor: '#1d4ed8',
      linkUnderline: true,
      entranceAnimation: 'none',
      animationDelay: 0,
      animationDuration: 600,
      animationTrigger: 'on-load',
      visibleOnDesktop: true,
      visibleOnTablet: true,
      visibleOnMobile: true,
      conditionalVisibility: false,
      conditionTag: '',
      ariaLabel: '',
      role: '',
      id: '',
      customClassName: '',
    },
    layout_config: { padding: 'lg', background: 'white', max_width: 'container' },
  },
  data_cards: {
    section_config: { card_style: 'elevated', show_pagination: false },
    layout_config: { padding: 'lg', background: 'surface', columns: 3 },
    data_binding: { source: 'table', table_slug: '', field_mapping: {}, limit: 6, sort: null },
  },
  data_table: {
    section_config: { show_search: true, page_size: 10 },
    layout_config: { padding: 'lg', background: 'white' },
    data_binding: { source: 'table', table_slug: '', field_mapping: {}, limit: 25 },
  },
  data_list: {
    section_config: { show_search: false },
    layout_config: { padding: 'lg', background: 'white' },
    data_binding: { source: 'table', table_slug: '', field_mapping: {}, limit: 10 },
  },
  data_timeline: {
    section_config: { order: 'desc' },
    layout_config: { padding: 'lg', background: 'surface' },
    data_binding: { source: 'table', table_slug: '', field_mapping: {}, limit: 20 },
  },
  chart_bar: {
    section_config: { title: '', show_legend: true },
    layout_config: { padding: 'lg', background: 'white' },
    data_binding: { source: 'table', table_slug: '', field_mapping: {}, limit: 50 },
  },
  chart_line: {
    section_config: { title: '', show_legend: true },
    layout_config: { padding: 'lg', background: 'white' },
    data_binding: { source: 'table', table_slug: '', field_mapping: {}, limit: 50 },
  },
  stat_widgets: {
    section_config: { widgets: [] },
    layout_config: { padding: 'lg', background: 'surface' },
  },
  image_banner: {
    section_config: {
      imageUrl: '',
      imageAlt: '',
      imageFit: 'cover',
      imagePosition: 'center',
      imagePositionX: 50,
      imagePositionY: 50,
      imageRepeat: 'no-repeat',
      imageParallax: false,
      imageParallaxSpeed: 0.4,
      imageLazyLoad: true,
      imageHoverZoom: false,
      imageHoverZoomScale: 1.08,
      imageHoverTransitionDuration: 400,
      width: 'full',
      height: 'auto',
      heightPx: 400,
      heightMobile: 220,
      heightTablet: 320,
      aspectRatio: 'none',
      customAspectRatioX: 16,
      customAspectRatioY: 9,
      maxWidth: 'none',
      maxWidthPx: 1400,
      overlayVisible: false,
      overlayType: 'solid',
      overlayColor: '#000000',
      overlayOpacity: 40,
      overlayGradientFrom: '#000000',
      overlayGradientTo: '#7c3aed',
      overlayGradientAngle: 135,
      overlayGradientType: 'linear',
      overlayBlendMode: 'normal',
      textOverlayVisible: false,
      textOverlayLayout: 'centered',
      textOverlayPositionX: 50,
      textOverlayPositionY: 50,
      badgeVisible: false,
      badgeText: 'New',
      badgeColor: '#7c3aed',
      headlineText: 'Your headline here',
      headlineFontSize: 40,
      headlineFontWeight: 700,
      headlineFontFamily: '',
      headlineColor: '#ffffff',
      subheadlineVisible: true,
      subheadlineText: 'A short supporting sentence.',
      subheadlineFontSize: 18,
      subheadlineColor: '#e2e8f0',
      textMaxWidth: 640,
      textPadding: 24,
      textAlignment: 'center',
      textShadow: false,
      textShadowColor: '#000000',
      textShadowBlur: 8,
      ctaVisible: false,
      primaryCTA: { label: 'Get Started', url: '/sign-up', openNewTab: false, style: 'filled', color: '#7c3aed', textColor: '#ffffff', size: 'md', icon: '', iconPosition: 'left' },
      secondaryCTA: { visible: false, label: 'Learn More', url: '#', openNewTab: false, style: 'outlined', color: '#ffffff', textColor: '#ffffff', size: 'md' },
      ctaLayout: 'side-by-side',
      ctaMarginTop: 24,
      borderVisible: false,
      borderColor: '#e2e8f0',
      borderWidth: 1,
      borderStyle: 'solid',
      borderRadius: 0,
      borderRadiusPerCorner: false,
      borderRadiusTopLeft: 0,
      borderRadiusTopRight: 0,
      borderRadiusBottomLeft: 0,
      borderRadiusBottomRight: 0,
      boxShadow: 'none',
      boxShadowColor: '#000000',
      boxShadowX: 0,
      boxShadowY: 8,
      boxShadowBlur: 24,
      boxShadowSpread: 0,
      shapeDividerBottom: 'none',
      shapeDividerColor: '#ffffff',
      shapeDividerHeight: 60,
      clipPath: 'none',
      clipPathCustomValue: '',
      marginTop: 0,
      marginBottom: 0,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      sectionWidth: 'full-bleed',
      bannerLinkUrl: '',
      bannerLinkOpenNewTab: false,
      cursorStyle: 'default',
      hoverOverlayColor: '#000000',
      hoverOverlayOpacity: 0,
      hoverScale: 1.0,
      hoverTransitionDuration: 300,
      lightboxEnabled: false,
      captionVisible: false,
      captionText: '',
      captionPosition: 'below',
      captionFontSize: 13,
      captionColor: '#64748b',
      captionAlignment: 'center',
      creditVisible: false,
      creditText: '',
      creditUrl: '',
      entranceAnimation: 'none',
      animationDelay: 0,
      animationDuration: 600,
      animationTrigger: 'on-scroll',
      visibleOnDesktop: true,
      visibleOnTablet: true,
      visibleOnMobile: true,
      mobileImageUrl: '',
      tabletImageUrl: '',
      ariaLabel: '',
      role: 'img',
      id: '',
      customClassName: '',
    },
    layout_config: { padding: 'none', background: 'white', max_width: 'full' },
  },
  divider: {
    section_config: {
      style: 'solid',
      color: null,
      thickness: 1,
      width: '80',
      spacing_top: 'md',
      spacing_bottom: 'md',
    },
    layout_config: { padding: 'none', background: 'white' },
  },
  form_builder: {
    section_config: {
      form_title: 'Contact Us',
      button_text: 'Submit',
      button_color: '',
      success_message: 'Thank you! Your submission has been received.',
      display_mode: 'inline',
      rowLayoutEnabled: false,
      defaultFieldsPerRow: 2,
      fields: [],
      rows: [],
      stagesEnabled: false,
      stages: [],
      persistenceEnabled: false,
      persistenceKey: '',
      persistenceFields: 'all',
      persistenceExpiry: 24,
      restoreOnLoad: true,
      restoreBannerVisible: true,
      restoreBannerText: 'You have a saved draft. Continue where you left off?',
      restoreBannerContinueLabel: 'Continue',
      restoreBannerStartOverLabel: 'Start over',
      cancelButtonVisible: false,
      cancelLabel: 'Cancel',
      cancelStyle: 'ghost',
      cancelPosition: 'inline-with-nav',
      cancelAction: 'clear-form',
      progressIndicatorStyle: 'steps',
      progressPosition: 'top',
      progressConnectorVisible: true,
      progressShowLabels: true,
      progressAnimated: true,
      summaryEditButtonVisible: true,
      summaryEditLabel: 'Edit',
      summaryMaskSensitiveFields: true,
      activeDesignId: 'default',
      designs: [],
    },
    layout_config: { padding: 'lg', background: 'white', max_width: 'container' },
  },
  html_css_workspace: {
    section_config: {
      html: '<div class="custom-section">\n  <h2>Custom Section</h2>\n  <p>Edit this HTML to create your custom content.</p>\n</div>',
      css: '.custom-section {\n  padding: 2rem;\n  text-align: center;\n  color: var(--primary-color);\n}',
      js: '',
    },
    layout_config: { padding: 'none', background: 'white' },
  },
  public_page: {
    section_config: {
      table_slug: '',
      view_type_override: '',
      initial_count: 6,
      detail_enabled: true,
      show_page_header: false,
      section_title: '',
      section_description: '',
      heading_align: 'left',
      show_more: true,
      show_more_text: 'View All',
      show_more_style: 'outline',
      show_more_align: 'center',
      bg_color: '',
      padding: 'none',
    },
    layout_config: {},
  },
};

// ─── Reducer ──────────────────────────────────────────────────────────────────

function newSection(type, order) {
  const defaults = SECTION_DEFAULTS[type] || {};
  return {
    _clientId: uuidv4(),
    id: null,
    section_type: type,
    order,
    is_visible: true,
    responsive_config: { hide_on: [] },
    layout_config: defaults.layout_config || {},
    data_binding: defaults.data_binding || null,
    section_config: defaults.section_config || {},
  };
}

function applyReducer(sections, action) {
  switch (action.type) {
    case 'SET_SECTIONS':
      return action.sections;

    case 'ADD_SECTION': {
      const insertAt = action.index ?? sections.length;
      const base = newSection(action.sectionType, insertAt);
      // Allow a template (or any caller) to pass config overrides
      const override = action.configOverride || {};
      const section = {
        ...base,
        ...(override.section_config  && { section_config:  { ...base.section_config,  ...override.section_config  } }),
        ...(override.layout_config   && { layout_config:   { ...base.layout_config,   ...override.layout_config   } }),
        ...(override.data_binding    && { data_binding:    override.data_binding }),
      };
      const next = [
        ...sections.slice(0, insertAt),
        section,
        ...sections.slice(insertAt),
      ].map((s, i) => ({ ...s, order: i }));
      return next;
    }

    case 'REMOVE_SECTION':
      return sections
        .filter(s => s._clientId !== action.clientId)
        .map((s, i) => ({ ...s, order: i }));

    case 'REORDER_SECTIONS': {
      // action.fromIndex → action.toIndex
      const list = [...sections];
      const [moved] = list.splice(action.fromIndex, 1);
      list.splice(action.toIndex, 0, moved);
      return list.map((s, i) => ({ ...s, order: i }));
    }

    case 'UPDATE_SECTION':
      return sections.map(s =>
        s._clientId === action.clientId
          ? { ...s, ...action.patch }
          : s
      );

    case 'UPDATE_SECTION_CONFIG':
      return sections.map(s =>
        s._clientId === action.clientId
          ? { ...s, section_config: { ...s.section_config, ...action.patch } }
          : s
      );

    case 'UPDATE_LAYOUT_CONFIG':
      return sections.map(s =>
        s._clientId === action.clientId
          ? { ...s, layout_config: { ...s.layout_config, ...action.patch } }
          : s
      );

    case 'UPDATE_DATA_BINDING':
      return sections.map(s =>
        s._clientId === action.clientId
          ? { ...s, data_binding: { ...(s.data_binding || {}), ...action.patch } }
          : s
      );

    default:
      return sections;
  }
}

const MAX_HISTORY = 50;

function historyReducer(state, action) {
  const { past, present, future } = state;

  if (action.type === 'UNDO') {
    if (past.length === 0) return state;
    const previous = past[past.length - 1];
    return {
      past: past.slice(0, -1),
      present: previous,
      future: [present, ...future],
    };
  }

  if (action.type === 'REDO') {
    if (future.length === 0) return state;
    const next = future[0];
    return {
      past: [...past, present],
      present: next,
      future: future.slice(1),
    };
  }

  // SELECT_SECTION doesn't push history
  if (action.type === 'SELECT_SECTION') {
    return state;
  }

  const newPresent = applyReducer(present, action);
  if (newPresent === present) return state;

  return {
    past: [...past.slice(-MAX_HISTORY), present],
    present: newPresent,
    future: [],
  };
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function usePageBuilder(initialSections = []) {
  const normalised = initialSections.map((s, i) => ({
    ...s,
    _clientId: s._clientId || uuidv4(),
    order: s.order ?? i,
  }));

  const [history, dispatch] = useReducer(historyReducer, {
    past: [],
    present: normalised,
    future: [],
  });

  const savedSnapshotRef = useRef(JSON.stringify(normalised));
  const sections = history.present;
  const isDirty = JSON.stringify(sections) !== savedSnapshotRef.current;

  const markSaved = useCallback(() => {
    savedSnapshotRef.current = JSON.stringify(history.present);
  }, [history.present]);

  const setSections = useCallback((secs) => {
    const normalised2 = secs.map((s, i) => ({
      ...s,
      _clientId: s._clientId || uuidv4(),
      order: s.order ?? i,
    }));
    savedSnapshotRef.current = JSON.stringify(normalised2);
    dispatch({ type: 'SET_SECTIONS', sections: normalised2 });
  }, []);

  return {
    sections,
    isDirty,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    dispatch,
    markSaved,
    setSections,
  };
}
